import { createFileRoute, getRouteApi, useNavigate } from "@tanstack/react-router";
import type { CurrentUser } from "../../lib/auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, useCallback } from "react";

import { api } from "../../lib/api";
import { wabaApi } from "../../lib/wabaApi";
import { createWsClient } from "../../lib/ws";
import { TabExecucoes } from "../../components/agent/TabExecucoes";
import { DialogPanel } from "../../components/shared/DialogPanel";
import { AlertTriangleIcon, ClockIcon } from "../../components/shared/Icons";
import { backdropClose } from "../../components/shared/backdrop";

const _authedRoute = getRouteApi("/_authed");

export const Route = createFileRoute("/_authed/")({
  component: DashboardPage,
});

interface Summary {
  total: number;
  errors: number;
  running: number;
  error_rate: number;
  prev_total: number;
  prev_errors: number;
  prev_error_rate: number;
  hours: number;
}

interface ProjectsData {
  overview: { total: number; active: number; development: number; inactive: number };
  by_manager: { name: string; count: number; active?: number; development?: number; inactive?: number }[];
}

interface ErrorBreakdownItem { manager: string; error_count: number; pct: number; }
interface ErrorBreakdown { items: ErrorBreakdownItem[]; total_errors: number; hours: number; }

// ── helpers ───────────────────────────────────────────────────────────────────

function trend(current: number, prev: number, lowerIsBetter: boolean, ppMode = false) {
  if (current === prev) return null;
  const isUp = current > prev;
  const color = lowerIsBetter
    ? (isUp ? "var(--danger)" : "var(--ok)")
    : (isUp ? "var(--ok)" : "var(--ink-muted)");
  let pct: string;
  if (ppMode) {
    const diff = Math.abs(current - prev);
    pct = `${diff % 1 === 0 ? diff : diff.toFixed(1)}pp`;
  } else {
    pct = prev === 0
      ? `+${current}`
      : `${Math.abs(Math.round(((current - prev) / prev) * 100))}%`;
  }
  return { arrow: isUp ? "↑" : "↓", pct, color };
}

function TrendBadge({ badge }: { badge?: ReturnType<typeof trend> }) {
  if (!badge) return null;
  return (
    <div style={{ position: "absolute", bottom: 10, right: 14, fontSize: 11, fontWeight: 600, color: badge.color, display: "flex", alignItems: "center", gap: 2 }}>
      <span>{badge.arrow}</span><span>{badge.pct}</span>
      <span style={{ fontSize: 9, color: "var(--ink-dim)", fontWeight: 400, marginLeft: 2 }}>7d</span>
    </div>
  );
}

// ── SummaryCard ───────────────────────────────────────────────────────────────

function SummaryCard({ label, value, color, badge, onClick, clickable }: {
  label: string; value: string | number; color: string;
  badge?: ReturnType<typeof trend>; onClick?: () => void; clickable?: boolean;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        flex: 1, minWidth: 150, background: "var(--surface-hover)", border: "1px solid var(--line)",
        borderRadius: 8, padding: "16px 20px", position: "relative",
        cursor: clickable ? "pointer" : "default",
        transition: clickable ? "border-color 0.15s" : undefined,
      }}
      onMouseOver={e => { if (clickable) e.currentTarget.style.borderColor = `${color}55`; }}
      onMouseOut={e => { if (clickable) e.currentTarget.style.borderColor = "var(--line)"; }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</div>
        {clickable && (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--ink-dim)" strokeWidth="2">
            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
            <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
        )}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      <TrendBadge badge={badge} />
    </div>
  );
}

// ── ErrorBreakdownPopup ───────────────────────────────────────────────────────

function ErrorBreakdownPopup({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = useQuery<ErrorBreakdown>({
    queryKey: ["dashboard-error-breakdown"],
    queryFn: () => api.get("/dashboard/error-breakdown?hours=168"),
  });

  return (
    <div {...backdropClose(() => { onClose(); })}
      style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "var(--z-modal)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <DialogPanel onClose={() => onClose()} label="Contribuição de Erros" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, width: 480, maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "var(--shadow-modal)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--line)", flexShrink: 0 }}>
          <div>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Contribuição de Erros</span>
            <span style={{ fontSize: 11, color: "var(--ink-muted)", marginLeft: 8 }}>últimos 7 dias</span>
          </div>
          <button onClick={onClose} style={{ color: "var(--ink-muted)", background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 4, display: "flex" }}
            onMouseOver={e => (e.currentTarget.style.color = "var(--ink)")} onMouseOut={e => (e.currentTarget.style.color = "var(--ink-muted)")}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
          {isLoading && <span style={{ fontSize: 13, color: "var(--ink-dim)" }}>Carregando...</span>}
          {!isLoading && data?.items.length === 0 && <span style={{ fontSize: 13, color: "var(--ink-dim)" }}>Nenhum dado.</span>}
          {data?.items.map(item => (
            <div key={item.manager} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 140, flexShrink: 0, fontSize: 13, color: "var(--ink-strong)", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6, padding: "4px 10px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.manager}
              </div>
              <div style={{ flex: 1, background: "var(--surface-input)", borderRadius: 4, height: 8, overflow: "hidden" }}>
                <div style={{ width: `${item.pct}%`, background: item.error_count > 0 ? "var(--danger)" : "var(--line)", height: "100%", borderRadius: 4 }} />
              </div>
              <div style={{ textAlign: "right", flexShrink: 0, minWidth: 60 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: item.error_count > 0 ? "var(--danger)" : "var(--ink-dim)" }}>{item.error_count}</span>
                {item.error_count > 0 && <span style={{ fontSize: 11, color: "var(--ink-muted)", marginLeft: 6 }}>{item.pct}%</span>}
              </div>
            </div>
          ))}
          {data && data.total_errors > 0 && (
            <div style={{ borderTop: "1px solid var(--line)", paddingTop: 10, marginTop: 4, display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--ink-muted)" }}>
              <span>Total</span>
              <span style={{ fontWeight: 700, color: "var(--danger)" }}>{data.total_errors} erros</span>
            </div>
          )}
        </div>
      </DialogPanel>
    </div>
  );
}

// ── ExecStatsPopup ────────────────────────────────────────────────────────────

const PRESET_PERIODS = [
  { label: "24h", hours: 24 },
  { label: "7d",  hours: 168 },
  { label: "14d", hours: 336 },
  { label: "30d", hours: 720 },
  { label: "90d", hours: 2160 },
  { label: "6m",  hours: 4320 },
  { label: "12m", hours: 8760 },
];

function ExecStatsPopup({ onClose }: { onClose: () => void }) {
  const [presetHours, setPresetHours] = useState(168);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const usingRange = fromDate && toDate;
  const queryParams = usingRange
    ? `from_ts=${new Date(fromDate).toISOString()}&until_ts=${new Date(toDate + "T23:59:59").toISOString()}`
    : `hours=${presetHours}`;

  const { data: s, isLoading } = useQuery<Summary>({
    queryKey: ["popup-summary", queryParams],
    queryFn: () => api.get(`/dashboard/summary?${queryParams}`),
  });

  function clearRange() { setFromDate(""); setToDate(""); }

  // Descrição do período para o header
  function periodLabel() {
    if (usingRange) return `${fromDate} → ${toDate}`;
    const p = PRESET_PERIODS.find(p => p.hours === presetHours);
    return p ? `Últimos ${p.label}` : `Últimas ${presetHours}h`;
  }

  const cards = s ? [
    { label: "Execuções Totais", value: s.total, color: "var(--ink)" as const,
      badge: trend(s.total, s.prev_total, false) },
    { label: "Erros",            value: s.errors, color: "var(--danger)" as const,
      badge: trend(s.errors, s.prev_errors, true) },
    { label: "Taxa de erro",     value: `${s.error_rate}%`, color: "var(--warn)" as const,
      badge: trend(s.error_rate, s.prev_error_rate, true, true) },
  ] : [];

  return (
    <div {...backdropClose(() => { onClose(); })}
      style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "var(--z-modal)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <DialogPanel onClose={() => onClose()} label="Análise Detalhada" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, width: 640, display: "flex", flexDirection: "column", boxShadow: "var(--shadow-modal)" }}>
        {/* header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--line)" }}>
          <div>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Análise Detalhada</span>
            <span style={{ fontSize: 11, color: "var(--ink-muted)", marginLeft: 8 }}>{periodLabel()}</span>
          </div>
          <button onClick={onClose} style={{ color: "var(--ink-muted)", background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 4, display: "flex" }}
            onMouseOver={e => (e.currentTarget.style.color = "var(--ink)")} onMouseOut={e => (e.currentTarget.style.color = "var(--ink-muted)")}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* period selector */}
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: 10 }}>
          {/* preset buttons */}
          <div style={{ display: "flex", gap: 6 }}>
            {PRESET_PERIODS.map(p => (
              <button key={p.hours} onClick={() => { setPresetHours(p.hours); clearRange(); }}
                style={{
                  padding: "5px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer",
                  border: `1px solid ${!usingRange && presetHours === p.hours ? "var(--violet-alt)" : "var(--line-strong)"}`,
                  background: !usingRange && presetHours === p.hours ? "rgba(108,71,255,0.15)" : "var(--surface-input)",
                  color: !usingRange && presetHours === p.hours ? "var(--violet)" : "var(--ink-mid)",
                }}
              >{p.label}</button>
            ))}
          </div>
          {/* date range */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>De</span>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              style={{ background: "var(--surface-input)", border: `1px solid ${usingRange ? "var(--violet-alt)" : "var(--line-strong)"}`, borderRadius: 6, padding: "5px 10px", fontSize: 12, color: "var(--ink)" }} />
            <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>até</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              style={{ background: "var(--surface-input)", border: `1px solid ${usingRange ? "var(--violet-alt)" : "var(--line-strong)"}`, borderRadius: 6, padding: "5px 10px", fontSize: 12, color: "var(--ink)" }} />
            {usingRange && (
              <button onClick={clearRange} style={{ fontSize: 11, color: "var(--ink-muted)", padding: "3px 8px", borderRadius: 4, background: "none", border: "none", cursor: "pointer" }}
                onMouseOver={e => (e.currentTarget.style.color = "var(--ink)")} onMouseOut={e => (e.currentTarget.style.color = "var(--ink-muted)")}>
                Limpar
              </button>
            )}
          </div>
        </div>

        {/* cards */}
        <div style={{ padding: "20px", display: "flex", gap: 12 }}>
          {isLoading ? (
            <span style={{ fontSize: 13, color: "var(--ink-dim)", padding: "20px 0" }}>Carregando...</span>
          ) : cards.map(card => (
            <div key={card.label} style={{ flex: 1, background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 8, padding: "16px 20px", position: "relative" }}>
              <div style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 }}>{card.label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: card.color }}>{card.value}</div>
              {card.badge && (
                <div style={{ position: "absolute", bottom: 10, right: 14, fontSize: 11, fontWeight: 600, color: card.badge.color, display: "flex", alignItems: "center", gap: 2 }}>
                  <span>{card.badge.arrow}</span><span>{card.badge.pct}</span>
                  <span style={{ fontSize: 9, color: "var(--ink-dim)", fontWeight: 400, marginLeft: 2 }}>ant.</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {s && (
          <div style={{ padding: "0 20px 14px", fontSize: 11, color: "var(--ink-dim)" }}>
            Comparando com o período anterior equivalente
          </div>
        )}
      </DialogPanel>
    </div>
  );
}

// ── ManagerStatusPopup ────────────────────────────────────────────────────────

const MANAGER_COLS = [
  { key: "count",       label: "Total",    color: "var(--ink)" },
  { key: "active",      label: "Ativos",   color: "var(--ok)" },
  { key: "development", label: "Desenv.",  color: "var(--info)" },
  { key: "inactive",    label: "Inativos", color: "var(--ink-muted)" },
] as const;

function ManagerStatusPopup({ projects, onClose }: { projects?: ProjectsData; onClose: () => void }) {
  const gridCols = "1fr repeat(4, 64px)";
  return (
    <div {...backdropClose(() => { onClose(); })}
      style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "var(--z-modal)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <DialogPanel onClose={() => onClose()} label="Status por Gestor" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, width: 540, maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "var(--shadow-modal)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--line)", flexShrink: 0 }}>
          <div>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Status por Gestor</span>
            <span style={{ fontSize: 11, color: "var(--ink-muted)", marginLeft: 8 }}>agentes por usuário do sistema</span>
          </div>
          <button onClick={onClose} style={{ color: "var(--ink-muted)", background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 4, display: "flex" }}
            onMouseOver={e => (e.currentTarget.style.color = "var(--ink)")} onMouseOut={e => (e.currentTarget.style.color = "var(--ink-muted)")}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 20px 16px" }}>
          <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 8, padding: "6px 0", borderBottom: "1px solid var(--line)" }}>
            <span style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>Gestor</span>
            {MANAGER_COLS.map(c => (
              <span key={c.key} style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", textAlign: "right" }}>{c.label}</span>
            ))}
          </div>
          {!projects?.by_manager.length && (
            <div style={{ fontSize: 12, color: "var(--ink-muted)", padding: "12px 0" }}>Nenhum dado</div>
          )}
          {projects?.by_manager.map(m => (
            <div key={m.name} style={{ display: "grid", gridTemplateColumns: gridCols, gap: 8, padding: "9px 0", borderBottom: "1px solid var(--surface-input)", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={m.name}>{m.name}</span>
              {MANAGER_COLS.map(c => (
                <span key={c.key} style={{ fontSize: 13, fontWeight: 600, color: c.color, textAlign: "right" }}>{m[c.key] ?? "—"}</span>
              ))}
            </div>
          ))}
          {projects && (
            <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 8, padding: "10px 0 2px", alignItems: "center" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-secondary)", textTransform: "uppercase", letterSpacing: ".04em" }}>Total</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", textAlign: "right" }}>{projects.overview.total}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ok)", textAlign: "right" }}>{projects.overview.active}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--info)", textAlign: "right" }}>{projects.overview.development}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink-muted)", textAlign: "right" }}>{projects.overview.inactive}</span>
            </div>
          )}
        </div>
      </DialogPanel>
    </div>
  );
}

// ── DashboardPage ─────────────────────────────────────────────────────────────

function DashboardPage() {
  const routeCtx = _authedRoute.useRouteContext() as { user?: CurrentUser };
  const currentUser = routeCtx.user;
  const canSeeProjByManager = currentUser?.role_name === "Admin" || !!currentUser?.permissions?.dash_geral || !!currentUser?.permissions?.viewer;
  const isViewer = !!currentUser?.permissions?.viewer;

  const qc = useQueryClient();
  const navigate = useNavigate();
  const [wsConnected, setWsConnected] = useState(false);
  const [errorPopupOpen, setErrorPopupOpen] = useState(false);
  const [execPopupOpen, setExecPopupOpen] = useState(false);
  const [managersPopupOpen, setManagersPopupOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const execDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Main cards: always 7 days, hardcoded
  const { data: summary } = useQuery<Summary>({
    queryKey: ["dashboard-summary"],
    queryFn: () => api.get("/dashboard/summary?hours=168"),
    refetchInterval: 30_000,
  });

  const { data: projects } = useQuery<ProjectsData>({
    queryKey: ["dashboard-projects"],
    queryFn: () => api.get("/dashboard/projects"),
    refetchInterval: 60_000,
  });

  const { data: alerts } = useQuery<{ active: string[]; pending: string[] }>({
    queryKey: ["agents-alerts"],
    queryFn: () => api.get("/agents/alerts"),
    refetchInterval: 20_000,
  });
  const alertingIds = alerts?.active ?? [];
  const pendingIds = alerts?.pending ?? [];

  const { data: unpub } = useQuery<{ unpublished: string[] }>({
    queryKey: ["agents-unpublished"],
    queryFn: () => api.get("/agents/unpublished"),
    refetchInterval: 60_000,
  });
  const unpublishedIds = unpub?.unpublished ?? [];

  // Conexões WABA desconectadas na Meta (coex morta) — visíveis ao usuário
  // conforme o escopo do próprio /connections (admin vê todas).
  const { data: wabaConns } = useQuery<{ meta_disconnected_at?: string | null }[]>({
    queryKey: ["waba-connections"],
    queryFn: () => wabaApi.get("/connections"),
    refetchInterval: 60_000,
    retry: false,
  });
  const disconnectedCount = (wabaConns ?? []).filter(c => c.meta_disconnected_at).length;

  // Estoques com erro no último redeploy — status só muda no redeploy, então lê ao abrir (sem polling).
  const { data: estoqueErr } = useQuery<{ errors: string[] }>({
    queryKey: ["estoque-errors"],
    queryFn: () => api.get("/estoque/errors"),
    retry: false,
  });
  const estoqueErrCount = estoqueErr?.errors.length ?? 0;

  const handleWsMessage = useCallback((data: unknown) => {
    const msg = data as { type?: string };
    if (msg.type !== "execution_update") return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
      qc.invalidateQueries({ queryKey: ["agents-alerts"] });
    }, 600);
    if (execDebounceRef.current) clearTimeout(execDebounceRef.current);
    execDebounceRef.current = setTimeout(() => {
      qc.invalidateQueries({ queryKey: ["executions", "__global__"] });
    }, 1500);
  }, [qc]);

  useEffect(() => {
    const client = createWsClient("/api/ws/executions", handleWsMessage, () => setWsConnected(true));
    return () => {
      client.close();
      setWsConnected(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (execDebounceRef.current) clearTimeout(execDebounceRef.current);
    };
  }, [handleWsMessage]);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflowY: "auto" }}>

      {execPopupOpen && <ExecStatsPopup onClose={() => setExecPopupOpen(false)} />}
      {errorPopupOpen && <ErrorBreakdownPopup onClose={() => setErrorPopupOpen(false)} />}
      {managersPopupOpen && <ManagerStatusPopup projects={projects} onClose={() => setManagersPopupOpen(false)} />}

      {/* Topbar */}
      <div style={{ height: 52, borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px", flexShrink: 0 }}>
        <h1 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Dashboard</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: wsConnected ? "var(--ok)" : "var(--ink-muted)" }} />
          <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>{wsConnected ? "Tempo real" : "Reconectando..."}</span>
        </div>
      </div>

      {/* Alerta de surto de erros — acima dos totais. Permanece até nenhum agente em alerta. */}
      {alertingIds.length > 0 && (
        <div style={{ padding: "16px 24px 0", flexShrink: 0 }}>
          <div
            onClick={() => navigate({ to: "/agents", search: { alerting: true } })}
            className="alert-pulse"
            style={{
              display: "flex", alignItems: "center", gap: 12,
              background: "var(--brand-soft)", border: "1px solid var(--danger)", borderRadius: 8,
              padding: "12px 16px", cursor: "pointer",
            }}
          >
            <span style={{ display: "inline-flex", lineHeight: 1, color: "var(--danger)" }}><AlertTriangleIcon size={18} /></span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--danger)" }}>
                {alertingIds.length} agente{alertingIds.length > 1 ? "s" : ""} com alerta de erros
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-mid)" }}>
                5+ erros em 10 min. Clique para ver os agentes afetados.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerta de conexão WABA desconectada na Meta (coex morta) — reconexão exige o cliente. */}
      {disconnectedCount > 0 && (
        <div style={{ padding: "16px 24px 0", flexShrink: 0 }}>
          <div
            onClick={() => navigate({ to: "/waba" })}
            className="alert-pulse"
            style={{
              display: "flex", alignItems: "center", gap: 12,
              background: "var(--danger-soft)", border: "1px solid var(--danger)", borderRadius: 8,
              padding: "12px 16px", cursor: "pointer",
            }}
          >
            <span style={{ display: "inline-flex", lineHeight: 1, color: "var(--danger)" }}><AlertTriangleIcon size={18} /></span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--danger)" }}>
                {disconnectedCount} conex{disconnectedCount > 1 ? "ões" : "ão"} WhatsApp desconectada{disconnectedCount > 1 ? "s" : ""} na Meta
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-mid)" }}>
                O número caiu do lado da Meta — mensagens não chegam. Clique para reconectar.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Estoques com erro no último redeploy — some sozinho quando o redeploy dá certo ou o estoque é excluído. */}
      {estoqueErrCount > 0 && (
        <div style={{ padding: "16px 24px 0", flexShrink: 0 }}>
          <div
            onClick={() => navigate({ to: "/estoque", search: { erro: true } })}
            style={{
              display: "flex", alignItems: "center", gap: 12,
              background: "var(--danger-soft)", border: "1px solid var(--danger)", borderRadius: 8,
              padding: "12px 16px", cursor: "pointer",
            }}
          >
            <span style={{ display: "inline-flex", lineHeight: 1, color: "var(--danger)" }}><AlertTriangleIcon size={18} /></span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--danger)" }}>
                {estoqueErrCount} estoque{estoqueErrCount > 1 ? "s" : ""} com erro na última atualização
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-mid)" }}>Clique para ver quais.</div>
            </div>
          </div>
        </div>
      )}

      {/* Alerta laranja — dismisses aguardando aprovação do admin. Escopado por visibilidade. */}
      {pendingIds.length > 0 && (
        <div style={{ padding: "16px 24px 0", flexShrink: 0 }}>
          <div
            onClick={() => navigate({ to: "/agents", search: { pending: true } })}
            style={{
              display: "flex", alignItems: "center", gap: 12,
              background: "var(--pending-soft)", border: "1px solid var(--pending)", borderRadius: 8,
              padding: "12px 16px", cursor: "pointer",
            }}
          >
            <span style={{ display: "inline-flex", lineHeight: 1, color: "var(--pending)" }}><ClockIcon size={18} /></span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--pending)" }}>
                {pendingIds.length} agente{pendingIds.length > 1 ? "s" : ""} aguardando aprovação de dismiss
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>
                Dismiss enviado pelo gestor. Clique para revisar (só Admin aprova).
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerta amarelo de projetos com modificações não publicadas */}
      {unpublishedIds.length > 0 && (
        <div style={{ padding: "16px 24px 0", flexShrink: 0 }}>
          <div
            onClick={() => navigate({ to: "/agents", search: { unpublished: true } })}
            className="publish-pulse"
            style={{
              display: "flex", alignItems: "center", gap: 12,
              background: "var(--warn-soft)", border: "1px solid var(--warn-line)", borderRadius: 8,
              padding: "12px 16px", cursor: "pointer",
            }}
          >
            <span style={{ display: "inline-flex", lineHeight: 1, color: "var(--warn)" }}><AlertTriangleIcon size={18} /></span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--warn)" }}>
                Você tem {unpublishedIds.length} projeto{unpublishedIds.length > 1 ? "s" : ""} com modificações não publicadas
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>
                Clique para ver os projetos que precisam publicar.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats cards — sempre 7 dias */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", padding: "20px 24px 0", flexShrink: 0 }}>
        <SummaryCard
          label="Execuções Totais"
          value={summary?.total ?? "—"}
          color="var(--ink)"
          badge={summary ? trend(summary.total, summary.prev_total, false) : null}
          clickable
          onClick={() => setExecPopupOpen(true)}
        />
        <SummaryCard
          label="Erros"
          value={summary?.errors ?? "—"}
          color="var(--danger)"
          badge={summary ? trend(summary.errors, summary.prev_errors, true) : null}
          clickable={canSeeProjByManager}
          onClick={canSeeProjByManager ? () => setErrorPopupOpen(true) : undefined}
        />
        <SummaryCard
          label="Taxa de erro"
          value={summary ? `${summary.error_rate}%` : "—"}
          color="var(--warn)"
          badge={summary ? trend(summary.error_rate, summary.prev_error_rate, true, true) : null}
          clickable={canSeeProjByManager}
          onClick={canSeeProjByManager ? () => setErrorPopupOpen(true) : undefined}
        />
      </div>

      {/* Projetos por Gestor + Visão Geral */}
      <div style={{ display: "flex", gap: 12, padding: "12px 24px 0", flexShrink: 0 }}>

        {canSeeProjByManager && (
        <div style={{ flex: 1, background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, padding: "16px 20px", maxHeight: 220, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 12 }}>Projetos por Gestor</div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {!projects?.by_manager.length && <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>Nenhum dado</span>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 24, rowGap: 8 }}>
              {projects?.by_manager.map(m => (
                <div key={m.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "var(--ink-strong)", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6, padding: "3px 10px", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.name}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", flexShrink: 0 }}>{m.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        )}

        <div
          onClick={() => { if (canSeeProjByManager) setManagersPopupOpen(true); }}
          onMouseOver={e => { if (canSeeProjByManager) e.currentTarget.style.borderColor = "var(--line-max)"; }}
          onMouseOut={e => { if (canSeeProjByManager) e.currentTarget.style.borderColor = "var(--line)"; }}
          style={{ flex: 1, background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, padding: "16px 20px", display: "flex", flexDirection: "column", cursor: canSeeProjByManager ? "pointer" : "default", transition: "border-color 0.15s" }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>Visão Geral</div>
            {canSeeProjByManager && (
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--ink-dim)" strokeWidth="2">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            )}
          </div>
          <div style={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "space-around" }}>
            {[
              { label: "TOTAL",      value: projects?.overview.total,       color: "var(--ink)" },
              { label: "ATIVOS",     value: projects?.overview.active,      color: "var(--ok)" },
              { label: "EM DESENV.", value: projects?.overview.development, color: "var(--info)" },
              { label: "INATIVOS",   value: projects?.overview.inactive,    color: "var(--ink-muted)" },
            ].map(s => (
              <div key={s.label} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value ?? "—"}</div>
                <div style={{ fontSize: 10, color: "var(--ink-muted)", marginTop: 4, letterSpacing: ".04em" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Execuções — filtro de data afeta apenas esta tabela. Oculto p/ viewer. */}
      {!isViewer && <TabExecucoes />}
    </div>
  );
}
